import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Invoice } from 'contracts/tracking-v2/invoices';
import type { CreateTripPayload } from 'contracts/tracking-v2/trips';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import {  InputGs, DateTimePickerGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';
import {  InvoicesTable } from './invoices-table'

type Ref = {
  open: (invoices: Invoice[]) => void;
  close: () => void;
}

type Props = {
  onSubmit: (data: CreateTripPayload) => void;
  loading?: boolean;
}

export type CreateTripDrawerRef = Ref

export const CreateTripDrawer = React.forwardRef<Ref, Props>(({
                                                                onSubmit,
                                                                loading,
                                                              }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const { deadlineDate, ...data } = formData
    onSubmit({
      invoiceIds: invoices.map((inv) => inv.id),
      deadlineDate: deadlineDate.toISOString(),
      ...data,
    });
  }, [onSubmit, invoices]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
    form.reset();
  }, [form]);

  React.useImperativeHandle(ref, () => ({
    open: (invoices) => {
      onOpenChange(invoices.length > 0);
      setInvoices(invoices);
    },
    close: () => {
      onOpenChange(false);
      setInvoices([]);
    },
  }), [onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className={'max-w-[1200px] px-6 w-full mx-auto'}>
          <Drawer.Header>
            <Drawer.Title className={'text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]'}>
              <span>Notas fiscais selecionadas</span>
              <span className={'font-extrabold text-gray-800 text-[40px]'}>
                {invoices.length.toString().padStart(3, '0')}
              </span>
            </Drawer.Title>
          </Drawer.Header>
          <InvoicesTable invoices={invoices} className={'mb-6'} />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className={'flex flex-col gap-4 pb-10'}
            >
              <div className={'grid grid-cols-2 xl:grid-cols-4 gap-4 pb-10'}>
                <FormField
                  control={form.control}
                  name={'cargoIdentifier'}
                  render={({ field }) => (
                    <InputGs {...field} type="text" label={'Id da carga'} />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'driverName'}
                  render={({ field }) => (
                    <InputGs {...field} type="text" label={'Nome do motorista'} />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'vehiclePlate'}
                  render={({ field }) => (
                    <InputGs {...field} type="text" label={'Placa do veículo'} />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'deadlineDate'}
                  render={({ field }) => (
                    <DateTimePickerGs
                      {...field}
                      label={'Prazo'}
                      fieldValue={field.value ? new Date(field.value) : undefined}
                      fieldOnChange={field.onChange}
                      portal={false}
                    />
                  )}
                />
              </div>
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Criar viagem'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});
CreateTripDrawer.displayName = 'CreateTripDrawer';